import React, { useMemo } from 'react';
import { Grid } from 'antd';
import { Values } from '@shared/utils/objectEnums';
import { formatCurrency } from '@shared/utils/currency';
import {
	Currencies,
	ExpensesSubjectToHireDaysIntervalLabels,
	ExpensesSubjectToHireDaysIntervals,
} from '@shared/utils/constants';
import { MeratusExpenseSubjectToHireDaysAccountCodes } from '@shared/utils/meratus';
import { ExpenseSubjectToHireDaysProps } from '@api/models/expense-subject-to-hire-days';
import { TcTermsWithIncludes } from '@api/utils/getTcTerms';
import EditableTable from '@client/components/EditableTable';
import {
	createExpenseSubjectToHireDays,
	deleteExpenseSubjectToHireDays,
	updateExpenseSubjectToHireDays,
} from '@client/lib/api';
import getMeratusAccountCodeColumn from '@client/utils/meratus/getMeratusAccountCodeColumn';
import useIsMeratusTcInOrOut from '@client/utils/meratus/useIsMeratusTcInOrOut';

const CveTable = ({
	refreshFixture,
	currency,
	locked = false,
	tcTerms,
	fixtureType,
}: {
	refreshFixture: () => void;
	currency: Values<typeof Currencies>;
	locked?: boolean;
	tcTerms: TcTermsWithIncludes;
	fixtureType?: string | null;
}) => {
	const screens = Grid.useBreakpoint();
	const isMeratusTcInOrOut = useIsMeratusTcInOrOut(fixtureType);
	
	const handleCreateExpenseSubjectToHireDays = async (
		values: {
			name: string;
			amount: number;
			interval: Values<typeof ExpensesSubjectToHireDaysIntervals>;
		},
	) => {
		await createExpenseSubjectToHireDays(tcTerms?.id, values);
		await refreshFixture();
	};

	const handleSaveExpenseSubjectToHireDays = async (
		id: number, values: {
			name: string;
			amount: number;
			interval: Values<typeof ExpensesSubjectToHireDaysIntervals>;
		},
	) => {
		await updateExpenseSubjectToHireDays(tcTerms?.id, id, values);
		await refreshFixture();
	};

	const handleDeleteExpenseSubjectToHireDays = async (id: number) => {
		await deleteExpenseSubjectToHireDays(tcTerms?.id, id);
		await refreshFixture();
	};

	const columns = useMemo(() => [
		{
			dataIndex: 'name',
			title: 'Name',
			editable: true,
		},
		{
			dataIndex: 'amount',
			title: 'Amount',
			editable: true,
			editingProps: {
				type: 'currency' as const,
				inputProps: {
					currency,
				},
			},
			render: (c: number) => c != null && (
				<>
					{formatCurrency(
						c,
						currency,
						{ forceDecimals: true },
					)}
				</>
			),
		},
		{
			dataIndex: 'interval',
			title: 'Interval',
			render: (interval: Values<typeof ExpensesSubjectToHireDaysIntervals>) => (
				ExpensesSubjectToHireDaysIntervalLabels[interval]
			),
			editable: true,
			editingProps: {
				type: 'select' as const,
				defaultValue: 'pcm',
				inputProps: {
					placeholder: 'Select interval',
				},
				options: [
					{ label: 'Per calendar month', value: 'pcm' },
					{ label: 'Per day', value: 'perDay' },
				],
			},
		},
		...(isMeratusTcInOrOut ? [
			getMeratusAccountCodeColumn<ExpenseSubjectToHireDaysProps, 'id'>(
				MeratusExpenseSubjectToHireDaysAccountCodes,
			),
		] : []),
	], [currency, isMeratusTcInOrOut]);

	return (
		<EditableTable
			key={tcTerms?.ExpensesSubjectToHireDays?.length}
			iconButtons
			pagination={false}
			enableDelete={!locked}
			enableEdit={!locked}
			allowAddNew={!locked}
			addNewText="Expenses subject to hire days"
			emptyText={(<>No expenses yet</>)}
			dataSource={tcTerms.ExpensesSubjectToHireDays ?? []}
			onSave={handleSaveExpenseSubjectToHireDays}
			onAddNew={handleCreateExpenseSubjectToHireDays}
			onDelete={handleDeleteExpenseSubjectToHireDays}
			keyDataIndex="id"
			useCards={screens.xs}
			columns={columns}
			addButtonTestId="cve-add-button"
		/>
	);
};

export default CveTable;
